/**
 * Kordaro v4 — navigatie per rol (lichte zijbalk + mobiele voet).
 *
 * Enkel WEERGAVE: dezelfde routes en dezelfde rolfilter (rolProfiel.toonInMenu) als voorheen.
 * Geen route, recht of startscherm wijzigt hier. De server blijft elke rechtencheck doen.
 *
 * - Desktop/tablet: alle toegestane modules met tekstlabel (meer dan 5 mag), Instellingen onderaan.
 * - Gsm (<768): maximaal 4 vaste bestemmingen + "Meer" met de rest (niets verdwijnt).
 */
import { toonInMenu, rolProfiel, type MenuPad } from './rolProfiel.js';
import type { KIconNaam } from '../shared/components/KIcon.js';

export interface NavDoel {
  pad: MenuPad;
  label: string;
  icoon: KIconNaam;
  /** Teller uit bestaande bronnen (ongelezen gesprekken / logistieke updates). */
  badge?: 'gesprekken' | 'logistiek';
}

/** Labels die een winkel zelf kan instellen (Instellingen → modulenamen). */
export interface NavLabels {
  communicatie?: string;
  crm?: string;
  buitendienst?: string;
  logistiek?: string;
  planning?: string;
}

/** De standaardnaam "Communicatie" heet in v4 "Gesprekken"; een eigen winkelnaam blijft staan. */
export function gesprekkenLabel(ingesteld?: string): string {
  return !ingesteld || ingesteld === 'Communicatie' ? 'Gesprekken' : ingesteld;
}

function doelen(l: NavLabels): Record<MenuPad, NavDoel> {
  return {
    '/dashboard':    { pad: '/dashboard',    label: 'Dashboard',                      icoon: 'dashboard' },
    '/communicatie': { pad: '/communicatie', label: gesprekkenLabel(l.communicatie),  icoon: 'gesprekken', badge: 'gesprekken' },
    '/crm':          { pad: '/crm',          label: l.crm || 'CRM',                   icoon: 'persoon' },
    '/projecten':    { pad: '/projecten',    label: 'Projecten',                      icoon: 'dossiers' },
    '/buitendienst': { pad: '/buitendienst', label: l.buitendienst || 'Buitendienst', icoon: 'plaatsing' },
    '/logistiek':    { pad: '/logistiek',    label: l.logistiek || 'Logistiek',       icoon: 'logistiek', badge: 'logistiek' },
    '/marketing':    { pad: '/marketing',    label: 'Marketing',                      icoon: 'marketing' },
    '/planning':     { pad: '/planning',     label: l.planning || 'Planning',         icoon: 'planning' },
    '/service':      { pad: '/service',      label: 'Service',                        icoon: 'service' },
    '/catalogus':    { pad: '/catalogus',    label: 'Catalogus',                      icoon: 'catalogus' },
    '/settings':     { pad: '/settings',     label: 'Instellingen',                   icoon: 'instellingen' },
  };
}

const VOLGORDE_BEHEER: MenuPad[] = ['/dashboard', '/communicatie', '/crm', '/projecten', '/buitendienst', '/logistiek', '/planning', '/service', '/marketing', '/catalogus'];
const VOLGORDE_VERKOPER: MenuPad[] = ['/crm', '/communicatie', '/projecten', '/planning', '/service', '/catalogus', '/buitendienst', '/logistiek', '/marketing', '/dashboard'];
const GSM_BEHEER: MenuPad[] = ['/dashboard', '/communicatie', '/crm', '/buitendienst'];
const GSM_VERKOPER: MenuPad[] = ['/crm', '/communicatie', '/projecten', '/planning'];

export interface Navigatie {
  /** Zijbalk, bovenaan. */
  hoofd: NavDoel[];
  /** Zijbalk, onderaan (Instellingen). */
  onder: NavDoel[];
  /** Mobiele voet: max 4. */
  voet: NavDoel[];
  /** Mobiel "Meer": al de rest. */
  meer: NavDoel[];
}

export function navigatieVoorRol(role: string | null | undefined, labels: NavLabels = {}): Navigatie {
  const d = doelen(labels);
  const isVerkoper = rolProfiel(role).start !== '/dashboard';
  const mag = (p: MenuPad) => toonInMenu(role, p);
  const volgorde = (isVerkoper ? VOLGORDE_VERKOPER : VOLGORDE_BEHEER).filter(mag);
  const hoofd = volgorde.map((p) => d[p]);
  const onder = mag('/settings') ? [d['/settings']] : [];
  const voet = (isVerkoper ? GSM_VERKOPER : GSM_BEHEER).filter(mag).slice(0, 4).map((p) => d[p]);
  const inVoet = new Set(voet.map((v) => v.pad));
  const meer = [...hoofd, ...onder].filter((v) => !inVoet.has(v.pad));
  return { hoofd, onder, voet, meer };
}

/** Actief als het pad ermee begint (zelfde regel als de oude zijbalk). */
export function isActiefPad(pathname: string, pad: string): boolean {
  return pathname.startsWith(pad);
}
